import React, { useCallback } from "react";
import { connect } from "react-redux";

import Menu from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { createCoordinates } from "@mapmycustomers/shared/util/geo/GeoService";

import AreaSearchMode from "@app/enum/AreaSearchMode";
import MapContextMenu from "@app/scene/map/components/MapContextMenu";
import {
  geocodeLocation,
  selectMapTool,
  setLeadFinderQuery,
  showLeadFinder,
  updatePinDropCoordinates,
} from "@app/scene/map/store/actions";
import { doesOrganizationSupportDemographicData, isLeadFinderEnabled } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

const messages = defineMessages({
  dropPin: {
    id: "map.boundaryOverlay.contextMenu.dropPin",
    defaultMessage: "Drop a pin",
    description: "Drop a pin menu item title",
  },
  findLeads: {
    id: "map.boundaryOverlay.contextMenu.findLeads",
    defaultMessage: "Find Leads near here",
    description: "Find Leads near here menu item title",
  },
  showDemographics: {
    id: "map.boundaryOverlay.contextMenu.showDemographics",
    defaultMessage: "Show demographics for this area",
    description: "Show demographics near here menu item title",
  },
});

interface Props {
  demographyEnabled: boolean;
  leadFinderEnabled: boolean;
  onGeocodeLocation: typeof geocodeLocation.request;
  onHide: () => void;
  onSelectMapTool: typeof selectMapTool;
  onSetLeadFinderQuery: typeof setLeadFinderQuery;
  onShowDemographyTooltip: (position: google.maps.LatLngLiteral) => void;
  onShowLeadFinder: typeof showLeadFinder;
  onUpdatePinDropCoordinates: typeof updatePinDropCoordinates.request;
  position: google.maps.LatLngLiteral | undefined;
}

const OverlayContextMenu: React.FC<Props> = ({
  demographyEnabled,
  leadFinderEnabled,
  onGeocodeLocation,
  onHide,
  onSelectMapTool,
  onSetLeadFinderQuery,
  onShowDemographyTooltip,
  onShowLeadFinder,
  onUpdatePinDropCoordinates,
  position,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Overlay Context Menu", useAnalytics());

  const handleDropPinClick = useCallback(() => {
    if (position) {
      const coordinates = createCoordinates({
        latitude: position.lat,
        longitude: position.lng,
      });
      onUpdatePinDropCoordinates(coordinates);
      onSelectMapTool({ mapTool: MapTool.DROP_PIN });
    }
    analytics.clicked(["Drop Pin"]);
  }, [analytics, position, onSelectMapTool, onUpdatePinDropCoordinates]);

  const handleFindLeadsClick = useCallback(() => {
    if (position) {
      const point: LongLat = [position.lng, position.lat];

      onSetLeadFinderQuery({
        address: undefined,
        location: undefined,
        mode: AreaSearchMode.ADDRESS,
        point: undefined,
      });

      onGeocodeLocation({
        point,
      });

      onShowLeadFinder();
    }
    analytics.clicked(["Find Leads"]);
  }, [analytics, onGeocodeLocation, onSetLeadFinderQuery, onShowLeadFinder, position]);

  const handleShowDemographics = useCallback(() => {
    if (position) {
      onShowDemographyTooltip(position);
    }
  }, [position, onShowDemographyTooltip]);

  return (
    <MapContextMenu onHide={onHide} showAt={position}>
      <Menu.Item key="dropPin" onClick={handleDropPinClick}>
        {intl.formatMessage(messages.dropPin)}
      </Menu.Item>

      {leadFinderEnabled && (
        <Menu.Item key="findLeads" onClick={handleFindLeadsClick}>
          {intl.formatMessage(messages.findLeads)}
        </Menu.Item>
      )}

      {demographyEnabled && (
        <Menu.Item key="showDemographics" onClick={handleShowDemographics}>
          {intl.formatMessage(messages.showDemographics)}
        </Menu.Item>
      )}
    </MapContextMenu>
  );
};

const mapStateToProps = (state: RootState) => ({
  demographyEnabled: doesOrganizationSupportDemographicData(state),
  leadFinderEnabled: isLeadFinderEnabled(state),
});

const mapDispatchToProps = {
  onGeocodeLocation: geocodeLocation.request,
  onSelectMapTool: selectMapTool,
  onSetLeadFinderQuery: setLeadFinderQuery,
  onShowLeadFinder: showLeadFinder,
  onUpdatePinDropCoordinates: updatePinDropCoordinates.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(OverlayContextMenu);
